    /**
 * @module features/devices/components/DeviceStatusBadge
 * @description Accessible status pill. Colour is never the only signal: every
 * status also carries a text label and a distinct dot shape/animation.
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { DeviceStatus } from "../types";

const STATUS_STYLES: Record<DeviceStatus, { label: string; pill: string; dot: string }> = {
  PROVISIONED: {
    label: "Provisioned",
    pill: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/60",
    dot: "bg-sky-500",
  },
  ACTIVE: {
    label: "Active",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60",
    dot: "bg-emerald-500",
  },
  OFFLINE: {
    label: "Offline",
    pill: "bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700",
    dot: "bg-zinc-400",
  },
  MAINTENANCE: {
    label: "Maintenance",
    pill: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/60",
    dot: "bg-amber-500",
  },
  DECOMMISSIONED: {
    label: "Decommissioned",
    pill: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/60",
    dot: "bg-red-500",
  },
};

export function statusLabel(status: DeviceStatus): string {
  return STATUS_STYLES[status]?.label ?? status;
}

interface DeviceStatusBadgeProps {
  status: DeviceStatus;
  className?: string;
}

export function DeviceStatusBadge({ status, className }: DeviceStatusBadgeProps) {
  const style = STATUS_STYLES[status];
  if (!style) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap",
        style.pill,
        className
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          style.dot,
          status === "ACTIVE" && "animate-pulse",
          status === "DECOMMISSIONED" && "rounded-[2px]"
        )}
      />
      {style.label}
    </span>
  );
}
